import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { PageHeader, Tabs } from "@/features/power_projects/components/ui";
import { usePageTitle } from "@/hooks/usePageTitle";
import { useLeadSummary } from "@/features/leads/api/leads";
import { useLeadSearches } from "@/features/leads/api/searches";
import { useQueueState } from "@/features/leads/api/campaigns";
import { useInbox } from "@/features/leads/api/inbox";

/**
 * Wspólna ramka modułu Leady — nagłówek i zakładki.
 *
 * Moduł ma JEDNĄ pozycję w menu; lista, zadania i lista blokująca to zakładki,
 * a nie osobne wpisy w nawigacji — to trzy widoki tej samej pracy, nie trzy
 * moduły. Adresy zostają osobne (`/leads/tasks`, `/leads/do-not-contact`), żeby
 * dało się wysłać komuś link do konkretnej zakładki.
 */

type TabKey = "list" | "search" | "campaigns" | "queue" | "inbox" | "tasks" | "funnel" | "dnc";

const PATHS: Record<TabKey, string> = {
  list: "/leads",
  search: "/leads/search",
  campaigns: "/leads/campaigns",
  queue: "/leads/queue",
  inbox: "/leads/inbox",
  tasks: "/leads/tasks",
  funnel: "/leads/funnel",
  dnc: "/leads/do-not-contact",
};

export function LeadsLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { data: summary } = useLeadSummary();
  const { data: searches } = useLeadSearches();

  // Licznik przy zakładce liczy kandydatów CZEKAJĄCYCH na decyzję — wyszukiwanie,
  // którego nikt nie przejrzał, jest tym samym co wyszukiwanie nieodpalone.
  const pendingCandidates = (searches ?? []).reduce(
    (sum, row) => sum + row.counts.proposed,
    0,
  );

  // Ile maili czeka w kolejce — licznik przy zakładce ma odpowiadać na „czy coś
  // stoi", bez wchodzenia w kampanie.
  const { data: queue } = useQueueState();
  const queuedMessages = queue?.queued ?? 0;

  // Odpowiedzi czekające na człowieka. To jedyny licznik w tym module, przy
  // którym po drugiej stronie ktoś realnie czeka — stąd czerwony.
  const { data: inbox } = useInbox();
  const waitingReplies = (inbox ?? []).length;

  usePageTitle("Leady");

  const active: TabKey = location.pathname.startsWith("/leads/search")
    ? "search"
    : location.pathname.startsWith("/leads/queue")
      ? "queue"
    : location.pathname.startsWith("/leads/campaigns")
      ? "campaigns"
    : location.pathname.startsWith("/leads/inbox")
      ? "inbox"
    : location.pathname.startsWith("/leads/funnel")
      ? "funnel"
    : location.pathname.startsWith("/leads/tasks")
      ? "tasks"
      : location.pathname.startsWith("/leads/do-not-contact")
        ? "dnc"
        : "list";

  return (
    <div className="mx-auto max-w-7xl p-4 md:p-6">
      <PageHeader
        title="Leady"
        subtitle="Firmy, których jeszcze nie mamy w systemie — kontakty, historia rozmów, zadania"
      />

      <Tabs
        active={active}
        onChange={(key) => navigate(PATHS[key])}
        tabs={[
          { key: "list", label: "Leady", count: summary?.total, countTone: "blue" },
          {
            key: "search",
            label: "Wyszukiwanie",
            count: pendingCandidates,
            countTone: "danger",
          },
          {
            key: "campaigns",
            label: "Kampanie",
            count: queuedMessages,
            countTone: "warn",
          },
          // Licznik ten sam co przy kampaniach — to ta sama kolejka, tyle że tutaj
          // widać ją firma po firmie i da się ustawić, kto pierwszy.
          {
            key: "queue",
            label: "Kolejka",
            count: queuedMessages,
            countTone: "warn",
          },
          {
            key: "inbox",
            label: "Skrzynka",
            count: waitingReplies,
            countTone: "danger",
          },
          {
            key: "tasks",
            label: "Zadania",
            count: summary?.my_open_tasks,
            countTone: summary?.my_overdue_tasks ? "danger" : "warn",
          },
          // Lejek na końcu: to ekran „czy to działa", czytany raz na jakiś czas,
          // a nie codzienna praca.
          { key: "funnel", label: "Lejek" },
          { key: "dnc", label: "Nie kontaktować", count: summary?.suppressed, countTone: "warn" },
        ]}
      />

      <Outlet />
    </div>
  );
}
